/**
 * PathObserver - 路径质量观察与换路决策中枢（单一决策者）
 *
 * 三条路径：direct(host 直连公网 TURN) / forward(host 经内网 forwarder) / p2p(直连)。
 * 数据源：
 *   - StatsService 'stats:pathQuality'（packetLoss 百分比；full 模式 ~1s / 低功耗模式 5s 一拍，不受状态栏可见性门控）
 *   - 'path:p2p_ready'（P2PAssist 打洞成功上报，机会驱动，不受 cooldown / 次数上限限制）
 *   - onStallSignal()（B4 视频卡死强触发，跳过劣化窗口 / cooldown / 次数上限直接决策）
 * 执行：
 *   - 目标 p2p     → p2pAssistService.performSwitchTo(sessionId)
 *   - 目标 relay   → pathMigrationService.startMigration(target)
 * 全部失败/被防护拦下 → 回调调用方传入的降级函数（原 B4 断流重 race）。
 *
 * 设计规格：docs/path_observer_design.md §4.1
 */

import { eventBus } from './EventBus';
import { signalingService } from './SignalingService';
import { p2pAssistService } from './P2PAssistService';
import { pathMigrationService } from './PathMigrationService';
import { webRTCService } from './WebRTCService';

export type PathId = 'direct' | 'forward' | 'p2p';

interface LedgerEntry {
  lossEwma: number;   // 丢包率 EWMA，0~1（0.15 = 15%）
  samples: number;
  lastUpdate: number;
  lastResult: 'ok' | 'degraded' | 'switch_ineffective';
}

type SwitchTrigger = 'loss' | 'stall' | 'p2p_ready';

// ===== 防护与判定参数（docs/path_observer_design.md §4.1）=====
const DEGRADE_LOSS_THRESHOLD = 0.05;       // 丢包 EWMA 阈值
const DEGRADE_WINDOW_MS = 5_000;           // 持续窗口
const DEGRADE_WINDOW_DEGRADED_MS = 5_000;  // 账本该路径 lastResult!=='ok' 时保持快速触发
const EWMA_ALPHA = 0.3;
const COOLDOWN_MS = 120_000;               // 普通 loss 自动换路后的冷却
const MAX_SWITCHES = 2;                    // 普通 loss 自动换路在 SWITCH_WINDOW_MS 内最多尝试次数
const SWITCH_WINDOW_MS = 600_000;
const HYSTERESIS_BACK_LOSS = 0.10;         // forward→direct 回切滞回：要求 forward EWMA > 10%
const VERIFY_WINDOW_MS = 30_000;           // 切换后验证窗口
const VERIFY_IMPROVE_THRESHOLD = 0.075;    // 验证窗口结束仍 > 7.5% → 切换无效
const VERIFY_MIN_SAMPLES = 10;             // 验证窗口至少需要的有效样本数，避免 stale EWMA 单点误判
const POST_SWITCH_STATS_GRACE_MS = 5_000;  // 换 PC 后跳过旧 StatsService EWMA 残留
const SUPPRESS_MS = 600_000;               // 无效方向抑制时长
const LEDGER_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 账本超过 24h 视为陈旧
const SUMMARY_INTERVAL_MS = 60_000;        // 正常态 quality tick 摘要间隔（劣化窗口内每 tick 打）
const P2P_SWITCH_TIMEOUT_MS = 30_000;      // p2p 切换看门狗：performSwitchTo 后无 adoptConnection 回执则判失败（防 inFlight 永久卡死）
const MAX_SWITCH_TOAST_THROTTLE_MS = 60_000; // 次数上限 toast 节流
const DEGRADED_BLOCKED_LOG_INTERVAL_MS = 10_000; // 劣化被防护拦下后降频输出

class PathObserver {
  private _log(...args: unknown[]) { console.log('[PathObserver]', ...args); }

  private started = false;
  private peerId = 'default';
  private ledger: Map<PathId, LedgerEntry> = new Map();

  // ----- 切换互斥与防护状态 -----
  private inFlight = false;              // 全局切换互斥（含 p2p 切换）
  private cooldownUntil = 0;             // 普通 loss 自动换路后 120s 冷却（跨重连保留，防 flip-flop）
  private switchHistory: number[] = [];  // 普通 loss 自动换路时间戳，10min 窗口（跨重连保留）
  private suppressed: Map<string, number> = new Map(); // "direct->forward" → 抑制解除时间

  // ----- 劣化窗口 / 验证窗口 -----
  private degradeSince = 0;
  private verifyUntil = 0;
  private verifyBaseline = 0;            // 切换时点旧路径 lossEwma
  private verifyPath: PathId | null = null;
  private verifyDirection = '';
  private verifySamples = 0;
  private statsIgnoreUntil = 0;
  private lastSummaryTs = 0;
  private lastDegradedBlockedLogAt = 0;
  private degradeCountdownActive = false; // 劣化倒计时卡片是否已弹出（仅弹出后才发 recovered/switching/dismiss）

  // ----- 在途切换上下文 -----
  private switchingFrom: PathId | null = null;
  private switchTarget: PathId | null = null;
  private switchTrigger: SwitchTrigger | null = null;
  private switchStartTs = 0;
  private p2pSwitchTimer: number | null = null;
  private p2pReadySessionId: string | null = null;
  private stallFallback: (() => void) | null = null; // B4 降级回调（切换失败/被拦时执行）
  private lastKnownPath: PathId = 'direct';          // 仅用于推断外部发起切换（手动 p2p）的 from 路径
  private lastMaxSwitchToastAt = 0;

  constructor() {
    // 数据源用 stats:pathQuality 而非 stats:update：后者只在 full collection 分支发射，
    // 会被状态栏可见性门控（隐藏 → 劣化检测失明）；前者低功耗模式也保持 5s 一拍
    eventBus.on('stats:pathQuality', (d) => this.handleStatsTick(d));
    eventBus.on('path:p2p_ready', (d) => this.onP2PReady(d.sessionId));
  }

  // currentPath 由 WebRTCService 维护，Observer 只读（§4.3）
  private get currentPath(): PathId { return webRTCService.currentPath; }

  /** 连接成功时由 WebRTCService 调用 */
  start(initialPath: PathId): void {
    this.peerId = signalingService.getRelayConfig()?.peerId ?? 'default';
    this.loadLedger();
    this.started = true;
    this.lastKnownPath = initialPath;
    this.degradeSince = 0;
    this.verifyUntil = 0;
    this.verifyPath = null;
    this.lastSummaryTs = Date.now();
    this._log(`started: path=${initialPath} peer=${this.peerId} ledger={${this.formatLedger()}}`);
  }

  /** 主连接断开/重连：清 inFlight 与计时，账本/冷却/切换历史保留 */
  reset(reason: string): void {
    const hadActivity = this.started || this.inFlight || this.verifyUntil > 0 || this.degradeSince > 0;
    this.started = false;
    this.inFlight = false;
    this.degradeSince = 0;
    this.verifyUntil = 0;
    this.verifyBaseline = 0;
    this.verifyPath = null;
    this.verifySamples = 0;
    this.statsIgnoreUntil = 0;
    this.switchingFrom = null;
    this.switchTarget = null;
    this.switchStartTs = 0;
    this.p2pReadySessionId = null;
    this.switchTrigger = null;
    this.stallFallback = null;
    if (this.p2pSwitchTimer !== null) { clearTimeout(this.p2pSwitchTimer); this.p2pSwitchTimer = null; }
    if (this.degradeCountdownActive) { this.degradeCountdownActive = false; eventBus.emit('path:degrade', { phase: 'dismiss' }); }
    if (!hadActivity) return; // 幂等：cleanup 路径可能重复触发，无活动时静默
    this._log(`reset: reason=${reason}`);
  }

  /**
   * B4 视频卡死信号（强触发）：跳过劣化窗口、cooldown、stall 间隔和次数上限直接决策。
   * fallback = 原 B4 excludeTurnHost+断流重 race 逻辑；切换被拦/失败时回调执行。
   */
  onStallSignal(fallback?: () => void): void {
    this._log('stall signal received from B4');
    if (!this.started) {
      if (fallback) { this._log('fallback to B4 reconnect: reason=observer_not_started'); fallback(); }
      return;
    }
    this.stallFallback = fallback ?? null;
    const executed = this.decideAndSwitch('stall');
    if (!executed) {
      const fb = this.stallFallback;
      this.stallFallback = null;
      if (fb) { this._log('fallback to B4 reconnect: reason=switch_blocked'); fb(); }
    }
  }

  /**
   * 切换完成回执：成功路径由两个来源驱动——
   *   p2p:   WebRTCService.adoptConnection(type='p2p')
   *   relay: PathMigrationService.startMigration 的 onDone 回调
   */
  onSwitchDone(newPath: PathId, ok: boolean, reason?: string): void {
    const now = Date.now();
    if (this.p2pSwitchTimer !== null) { clearTimeout(this.p2pSwitchTimer); this.p2pSwitchTimer = null; }
    const observerInitiated = this.switchTarget === newPath;
    const trigger = this.switchTrigger;
    const from = this.switchingFrom ?? this.lastKnownPath;
    const elapsed = this.switchStartTs > 0 ? now - this.switchStartTs : 0;
    this.inFlight = false;
    this.switchingFrom = null;
    this.switchTarget = null;
    this.switchTrigger = null;
    this.switchStartTs = 0;
    if (!this.started) { this.lastKnownPath = newPath; return; }

    if (ok) {
      // 只有普通 loss 自动换路占用频率额度和 cooldown；P2P ready / stall 不受这些保护限制。
      if (observerInitiated && trigger === 'loss') {
        this.switchHistory.push(now);
        this.cooldownUntil = now + COOLDOWN_MS;
      }
      this.verifyUntil = now + VERIFY_WINDOW_MS;
      this.verifyBaseline = this.ledger.get(from)?.lossEwma ?? 0;
      this.verifyPath = newPath;
      this.verifyDirection = `${from}->${newPath}`;
      this.verifySamples = 0;
      this.statsIgnoreUntil = now + POST_SWITCH_STATS_GRACE_MS;
      const newEntry = this.ledger.get(newPath);
      if (newEntry) {
        // 换 PC 后 StatsService 的 packetLoss EWMA 会带旧路径尾巴；新路径验证从干净账本开始。
        newEntry.lossEwma = 0;
        newEntry.samples = 0;
        newEntry.lastUpdate = now;
      }
      this.degradeSince = 0;
      // 任何成功切换都清 p2p 就绪缓存：切到 p2p 时该 session 已转正不可再切；
      // pm 切换（relay）时 host 端会关闭该 client 其余全部 session（含缓存的
      // p2p-assist session），缓存必然失效，残留会让后续劣化决策选中已死 session
      this.p2pReadySessionId = null;
      this.stallFallback = null;
      this.lastKnownPath = newPath;
      this._log(`switch done: now=${newPath} elapsed=${elapsed}ms`);
      this.persistLedger();
    } else {
      if (observerInitiated && trigger === 'loss') {
        this.switchHistory.push(now); // loss 失败也计入额度，防反复尝试
        this.cooldownUntil = now + COOLDOWN_MS;
      }
      if (newPath === 'p2p') this.p2pReadySessionId = null; // 失败的 p2p session 不再可信
      const fb = this.stallFallback;
      this.stallFallback = null;
      this._log(`switch FAILED: target=${newPath} reason=${reason ?? 'unknown'} → fallback=${fb ? 'b4_reconnect' : 'none'}`);
      if (fb) fb();
    }
  }

  // ===== 数据源：StatsService 质量 tick（full 模式 ~1s / 低功耗模式 5s 一拍）=====
  private handleStatsTick(data: { packetLoss: number }): void {
    if (!this.started) return;
    if (typeof data.packetLoss !== 'number') return;

    const now = Date.now();
    if (now < this.statsIgnoreUntil) return;
    const raw = Math.max(0, data.packetLoss) / 100; // StatsService 的 packetLoss 是百分比
    const path = this.currentPath;
    const entry = this.ledger.get(path)!;
    entry.lossEwma = entry.samples === 0 ? raw : EWMA_ALPHA * raw + (1 - EWMA_ALPHA) * entry.lossEwma;
    entry.samples++;
    entry.lastUpdate = now;
    if (this.verifyPath === path && this.verifyUntil > 0) {
      this.verifySamples++;
    }

    // 切换后验证窗口到期评估
    if (this.verifyUntil > 0 && now >= this.verifyUntil) {
      this.evaluateVerifyWindow(now);
    }

    // 劣化判定（迁移在途时暂停判定，避免切换期波动误判叠加触发）
    if (!this.inFlight && entry.lossEwma > DEGRADE_LOSS_THRESHOLD) {
      const windowMs = entry.lastResult !== 'ok' ? DEGRADE_WINDOW_DEGRADED_MS : DEGRADE_WINDOW_MS;
      if (this.degradeSince === 0) {
        this.degradeSince = now;
        // 开窗：质量刚变差 → 这次确实会切（未被防护拦下）才弹倒计时卡片，进度条走的就是这 windowMs
        const target = this.predictLossTarget();
        if (this.canLossSwitchNow(target)) {
          this.degradeCountdownActive = true;
          eventBus.emit('path:degrade', { phase: 'start', label: this.pathLabel(target), windowMs, startTs: now });
        }
      }
      const shouldLog =
        now - this.degradeSince < windowMs ||
        now - this.lastDegradedBlockedLogAt >= DEGRADED_BLOCKED_LOG_INTERVAL_MS;
      if (shouldLog) {
        this.lastDegradedBlockedLogAt = now;
        this._log(`quality tick: path=${path} lossEwma=${this.pct(entry.lossEwma)} raw=${this.pct(raw)}`);
      }
      if (now - this.degradeSince >= windowMs) {
        this._log(`degraded CONFIRMED: path=${path} lossEwma=${this.pct(entry.lossEwma)} threshold=${this.pct(DEGRADE_LOSS_THRESHOLD)} window=${windowMs / 1000}s`);
        entry.lastResult = 'degraded';
        this.persistLedger();
        this.degradeSince = 0; // 被防护拦下时重新开窗，周期性输出 blocked 日志而非每秒刷
        const switched = this.decideAndSwitch('loss');
        // 窗口熬满：弹过倒计时则按实际结果收尾——真切了转"正在切换"，被拦则撤下
        if (this.degradeCountdownActive) {
          if (switched) {
            eventBus.emit('path:degrade', { phase: 'switching', label: this.pathLabel(this.switchTarget ?? this.predictLossTarget()) });
          } else {
            eventBus.emit('path:degrade', { phase: 'dismiss' });
          }
          this.degradeCountdownActive = false;
        }
      }
    } else {
      if (this.degradeSince !== 0) {
        this.degradeSince = 0;
        // 窗口内质量回落 → 取消切换，撤下倒计时卡片
        if (this.degradeCountdownActive) {
          eventBus.emit('path:degrade', { phase: 'recovered' });
          this.degradeCountdownActive = false;
        }
      }
      if (now - this.lastSummaryTs >= SUMMARY_INTERVAL_MS) {
        this.lastSummaryTs = now;
        this._log(`quality tick: path=${path} lossEwma=${this.pct(entry.lossEwma)} raw=${this.pct(raw)}`);
      }
    }
  }

  // ===== 机会驱动：P2P 就绪（不受 cooldown / 次数上限保护）=====
  private onP2PReady(sessionId: string): void {
    if (!this.started) {
      // 必须留痕：部署后靠「p2p ready received → switching」链路核对，静默丢弃会造成无解释断点（设计 §6）
      this._log(`p2p ready received: session=${sessionId} ignored (observer not started)`);
      return;
    }
    if (this.currentPath === 'p2p') {
      this._log(`p2p ready received: session=${sessionId} ignored (already p2p)`);
      return;
    }
    const now = Date.now();
    const supUntil = this.suppressed.get(`${this.currentPath}->p2p`) ?? 0;
    if (supUntil > now) {
      this._log(`switch blocked: reason=suppressed(${this.currentPath}→p2p ${Math.ceil((supUntil - now) / 1000)}s left)`);
      return;
    }
    let note: string = this.currentPath;
    if (this.inFlight) {
      if (pathMigrationService.active) {
        // P2P 优先：中止在途 pm 迁移再切 P2P
        const pmSid = pathMigrationService.activeSessionId;
        pathMigrationService.abort('p2p_preempt');
        this.inFlight = false;
        this.switchingFrom = null;
        this.switchTarget = null;
        this.switchTrigger = null;
        note = `preempted pm session ${pmSid}`;
      } else {
        // p2p 切换已在途，忽略重复 ready（P2PAssist 多条连接相继达阈值时会重复上报）
        this._log(`p2p ready received: session=${sessionId} ignored (p2p switch already in flight)`);
        return;
      }
    }
    this.p2pReadySessionId = sessionId;
    this._log(`p2p ready received: session=${sessionId} → switching (${note})`);
    this.executeSwitch('p2p', 'p2p_ready');
  }

  // ===== 决策：目标选择 + 防护检查链 =====
  private decideAndSwitch(trigger: 'loss' | 'stall'): boolean {
    const now = Date.now();
    if (this.inFlight) {
      this._log('switch blocked: reason=in_flight');
      return false;
    }
    if (trigger === 'loss' && now < this.cooldownUntil) {
      this._log(`switch blocked: reason=cooldown(${Math.ceil((this.cooldownUntil - now) / 1000)}s left)`);
      return false;
    }
    this.pruneHistory();
    if (trigger === 'loss' && this.switchHistory.length >= MAX_SWITCHES) {
      this._log(`switch blocked: reason=max_switches(${this.switchHistory.length} in 10min)`);
      this.toastMaxSwitches();
      return false;
    }
    const cur = this.currentPath;
    // p2p 就绪缓存验活：host 侧可能已把该 session 清理（离线清理/其他切换关闭），
    // 死缓存会让 executeSwitch 白等 30s 看门狗，失效则清掉落回 relay 目标
    if (this.p2pReadySessionId && !p2pAssistService.hasEstablished(this.p2pReadySessionId)) {
      this._log(`p2p ready cache invalidated: session=${this.p2pReadySessionId} no longer established`);
      this.p2pReadySessionId = null;
    }
    let target: PathId;
    if (trigger === 'stall' && cur === 'forward') {
      target = 'forward'; // forward 上视频卡死优先重建同路径，不降级回 direct
    } else if (trigger === 'stall' && cur === 'direct') {
      target = 'forward';
    } else if (this.p2pReadySessionId && cur !== 'p2p') {
      target = 'p2p'; // P2P 就绪优先
    } else if (cur === 'p2p') {
      // p2p 劣化 → 账本优选 relay：取 direct/forward 中 lossEwma 较低者（平手取 direct）
      const d = this.ledger.get('direct')!.lossEwma;
      const f = this.ledger.get('forward')!.lossEwma;
      target = f < d ? 'forward' : 'direct';
    } else {
      target = cur === 'direct' ? 'forward' : 'direct';
    }

    const dir = `${cur}->${target}`;
    const supUntil = this.suppressed.get(dir) ?? 0;
    if (supUntil > now) {
      this._log(`switch blocked: reason=suppressed(${cur}→${target} ${Math.ceil((supUntil - now) / 1000)}s left)`);
      return false;
    }
    if (target !== 'p2p') {
      // 滞回：forward→direct 回切要求 forward 真的烂（防 direct/forward 来回摆）
      if (cur === 'forward' && target === 'direct') {
        const fwdEwma = this.ledger.get('forward')!.lossEwma;
        if (fwdEwma <= HYSTERESIS_BACK_LOSS) {
          this._log(`switch blocked: reason=hysteresis(fwd ewma ${this.pct(fwdEwma)}<${this.pct(HYSTERESIS_BACK_LOSS)})`);
          return false;
        }
      }
    }

    this._log(`switch decision: ${cur}→${target} trigger=${trigger}`);
    // loss 触发的用户提示由劣化倒计时卡片（path:degrade）统一接管，这里不再单独弹 toast
    return this.executeSwitch(target, trigger);
  }

  /** loss 触发的目标预测（不执行切换）：供劣化倒计时卡片提前显示"切到哪"，与 decideAndSwitch 目标选择一致。 */
  private predictLossTarget(): PathId {
    const cur = this.currentPath;
    if (this.p2pReadySessionId && p2pAssistService.hasEstablished(this.p2pReadySessionId) && cur !== 'p2p') return 'p2p';
    if (cur === 'p2p') {
      const d = this.ledger.get('direct')!.lossEwma;
      const f = this.ledger.get('forward')!.lossEwma;
      return f < d ? 'forward' : 'direct';
    }
    return cur === 'direct' ? 'forward' : 'direct';
  }

  /** loss 切换当前是否不会被防护拦下（cooldown/次数/抑制/滞回）——开窗时据此决定弹不弹倒计时，避免必被拦时空弹。 */
  private canLossSwitchNow(target: PathId): boolean {
    const now = Date.now();
    if (now < this.cooldownUntil) return false;
    this.pruneHistory();
    if (this.switchHistory.length >= MAX_SWITCHES) return false;
    if ((this.suppressed.get(`${this.currentPath}->${target}`) ?? 0) > now) return false;
    if (this.currentPath === 'forward' && target === 'direct' && this.ledger.get('forward')!.lossEwma <= HYSTERESIS_BACK_LOSS) return false;
    return true;
  }

  private executeSwitch(target: PathId, trigger: SwitchTrigger): boolean {
    this.switchingFrom = this.currentPath;
    this.switchTarget = target;
    this.switchTrigger = trigger;
    this.switchStartTs = Date.now();
    this.inFlight = true;

    if (target === 'p2p') {
      const sid = this.p2pReadySessionId!;
      this._log(`switch executing: target=p2p via=p2p_assist session=${sid}`);
      // 看门狗：P2PAssist prepare 静默超时只弹 toast 不回调，这里兜底解除互斥
      this.p2pSwitchTimer = window.setTimeout(() => {
        this.p2pSwitchTimer = null;
        if (this.inFlight && this.switchTarget === 'p2p') {
          this.onSwitchDone('p2p', false, 'p2p_switch_timeout');
        }
      }, P2P_SWITCH_TIMEOUT_MS);
      p2pAssistService.performSwitchTo(sid);
      return true;
    }

    const sid = pathMigrationService.startMigration(target, (ok, reason) => {
      this.onSwitchDone(target, ok, reason);
    });
    if (sid !== null) {
      this._log(`switch executing: target=${target} via=path_migrate session=${sid}`);
      return true;
    }
    return false; // startMigration 失败路径已同步回调 onDone(false) → onSwitchDone 已处理
  }

  // ===== 切换后验证窗口 =====
  private evaluateVerifyWindow(now: number): void {
    const path = this.verifyPath;
    this.verifyUntil = 0;
    this.verifyPath = null;
    if (!path) return;
    if (this.verifySamples < VERIFY_MIN_SAMPLES) {
      this.verifyPath = path;
      this.verifyUntil = now + VERIFY_WINDOW_MS;
      this._log(`verify delayed: path=${path} samples=${this.verifySamples}<${VERIFY_MIN_SAMPLES}`);
      return;
    }
    const entry = this.ledger.get(path)!;
    if (entry.lossEwma > VERIFY_IMPROVE_THRESHOLD) {
      entry.lastResult = 'switch_ineffective';
      this.suppressed.set(this.verifyDirection, now + SUPPRESS_MS);
      this._log(`verify INEFFECTIVE: path=${path} lossEwma still ${this.pct(entry.lossEwma)} → suppress ${this.verifyDirection.replace('->', '→')} ${SUPPRESS_MS / 1000}s`);
      eventBus.emit('ui:toast', { message: '切换线路后网络仍未改善，该方向自动换路已暂停10分钟', type: 'warning' });
    } else {
      entry.lastResult = 'ok';
      this._log(`verify OK: path=${path} lossEwma ${this.pct(this.verifyBaseline)}→${this.pct(entry.lossEwma)} (${VERIFY_WINDOW_MS / 1000}s)`);
    }
    this.verifyDirection = '';
    this.verifySamples = 0;
    this.persistLedger();
  }

  // ===== 工具 =====
  private pruneHistory(): void {
    const cutoff = Date.now() - SWITCH_WINDOW_MS;
    this.switchHistory = this.switchHistory.filter((t) => t >= cutoff);
  }

  private toastMaxSwitches(): void {
    const now = Date.now();
    if (now - this.lastMaxSwitchToastAt < MAX_SWITCH_TOAST_THROTTLE_MS) return;
    this.lastMaxSwitchToastAt = now;
    eventBus.emit('ui:toast', { message: '网络路径切换过于频繁，已暂停自动换路', type: 'warning' });
  }

  private pct(x: number): string {
    return `${(x * 100).toFixed(1)}%`;
  }

  // 面向用户 toast 的路径中文名（日志仍用英文 PathId 便于和代码对应）
  private pathLabel(p: PathId): string {
    return p === 'direct' ? '直连' : p === 'forward' ? '内网转发' : 'P2P直连';
  }

  private formatLedger(): string {
    return (['direct', 'forward', 'p2p'] as PathId[])
      .map((p) => {
        const e = this.ledger.get(p)!;
        return `${p}:${this.pct(e.lossEwma)}/${e.lastResult}`;
      })
      .join(', ');
  }

  // ===== 账本持久化（localStorage，key 含 peer id）=====
  private storageKey(): string { return `webrtcgo_pathLedger_${this.peerId}`; }

  private loadLedger(): void {
    const def = (): LedgerEntry => ({ lossEwma: 0, samples: 0, lastUpdate: 0, lastResult: 'ok' });
    this.ledger = new Map([['direct', def()], ['forward', def()], ['p2p', def()]]);
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return;
      const obj = JSON.parse(raw) as Record<string, Partial<LedgerEntry>>;
      for (const p of ['direct', 'forward', 'p2p'] as PathId[]) {
        const e = obj[p];
        if (e && typeof e.lossEwma === 'number') {
          const lastUpdate = typeof e.lastUpdate === 'number' ? e.lastUpdate : 0;
          if (lastUpdate > 0 && Date.now() - lastUpdate > LEDGER_MAX_AGE_MS) continue;
          this.ledger.set(p, {
            lossEwma: e.lossEwma,
            samples: typeof e.samples === 'number' ? e.samples : 0,
            lastUpdate,
            lastResult: e.lastResult === 'degraded' || e.lastResult === 'switch_ineffective' ? e.lastResult : 'ok',
          });
        }
      }
    } catch { /* 持久化数据损坏时用默认值 */ }
  }

  private persistLedger(): void {
    try {
      const obj: Record<string, LedgerEntry> = {};
      this.ledger.forEach((v, k) => { obj[k] = v; });
      localStorage.setItem(this.storageKey(), JSON.stringify(obj));
    } catch { /* 配额/隐私模式失败可忽略，账本只是优化依据 */ }
  }
}

// Singleton instance
export const pathObserver = new PathObserver();
export default pathObserver;
